import React, { useState } from "react";
import { Info, CheckCircle2, TrendingUp, ShoppingCart, CreditCard, FileText, Edit3 } from "lucide-react";

const EntryTypeStep = ({ selectedType, onSelect, onManualEntry }) => {
  const [hoveredType, setHoveredType] = useState(null);

  const entryTypes = [
    {
      id: "income",
      name: "Intäkter",
      subtitle: "Pengar in",
      description: "Försäljning, fakturor, betalningar från kunder",
      examples: ["Försäljningsintäkter", "Konsultarvoden", "Ränteintäkter", "Hyresintäkter"],
      icon: <TrendingUp size={24} />,
      color: "#10b981",
      bgColor: "#ecfdf5",
      borderColor: "#d1fae5",
      activeBgColor: "#d1fae5",
      activeBorderColor: "#10b981",
      commonScenarios: "Vanligt när du får betalt för produkter eller tjänster"
    },
    {
      id: "expense",
      name: "Kostnader",
      subtitle: "Pengar ut",
      description: "Inköp, kostnader, leverantörsbetalningar",
      examples: ["Kontorsmaterial", "Hyra", "Telefon", "Marknadsföring"],
      icon: <ShoppingCart size={24} />,
      color: "#ef4444",
      bgColor: "#fef2f2",
      borderColor: "#fecaca",
      activeBgColor: "#fecaca",
      activeBorderColor: "#ef4444",
      commonScenarios: "Vanligt för alla utgifter och inköp företaget gör"
    },
    {
      id: "transfer",
      name: "Överföringar",
      subtitle: "Mellan konton",
      description: "Flytta pengar mellan bankkonton eller kassor",
      examples: ["Bank till bank", "Kassa till bank", "Sparkonto", "Valutaväxling"],
      icon: <CreditCard size={24} />,
      color: "#3b82f6",
      bgColor: "#eff6ff",
      borderColor: "#dbeafe",
      activeBgColor: "#dbeafe",
      activeBorderColor: "#3b82f6",
      commonScenarios: "När du flyttar pengar utan att påverka resultat"
    },
    {
      id: "other",
      name: "Övrigt",
      subtitle: "Specialposter",
      description: "Justeringar, rättelser eller andra speciella poster",
      examples: ["Avskrivningar", "Periodiseringar", "Rättelser", "Valutajusteringar"],
      icon: <FileText size={24} />,
      color: "#8b5cf6",
      bgColor: "#faf5ff",
      borderColor: "#e9d5ff",
      activeBgColor: "#e9d5ff",
      activeBorderColor: "#8b5cf6",
      commonScenarios: "För avancerade bokföringsposter och justeringar"
    },
  ];

  return (
    <div className="enhanced-step-content">
      {/* Header Section */}
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>
          Välj Posttyp
        </h2>
        <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '1rem' }}>
          Vilken typ av bokföringspost vill du skapa?
        </p>

        <div className="info-card">
          <Info className="info-icon" size={20} />
          <p className="info-text">
            <strong>Osäker på vad du ska välja?</strong> Oroa dig inte! De flesta poster är antingen intäkter (pengar in) eller kostnader (pengar ut).
            Hovra över korten för att se fler exempel.
          </p>
        </div>
      </div>

      {/* Entry Type Grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: window.innerWidth > 1200 ? 'repeat(4, 1fr)' : 'repeat(2, 1fr)',
        gap: '0.75rem',
        marginBottom: '2rem'
      }}>
        {entryTypes.map((type) => {
          const isSelected = selectedType === type.id;
          const isHovered = hoveredType === type.id;

          return (
            <div
              key={type.id}
              style={{
                backgroundColor: isSelected ? type.activeBgColor : type.bgColor,
                borderColor: isSelected ? type.activeBorderColor : type.borderColor,
                borderRadius: '8px',
                border: '2px solid',
                padding: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: isSelected ? 'translateY(-2px)' : isHovered ? 'translateY(-2px)' : 'translateY(0)',
                boxShadow: isSelected ? '0 4px 12px rgba(0, 0, 0, 0.1)' : isHovered ? '0 4px 12px rgba(0, 0, 0, 0.08)' : 'none',
                minHeight: '160px'
              }}
              onClick={() => onSelect(type.id)}
              onMouseEnter={() => setHoveredType(type.id)}
              onMouseLeave={() => setHoveredType(null)}
            >
              {/* Card Header - More compact */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.625rem' }}>
                <div
                  style={{
                    padding: '0.375rem',
                    borderRadius: '6px',
                    backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
                    color: type.color,
                    flexShrink: 0,
                    transform: isHovered ? 'scale(1.05)' : 'scale(1)',
                    transition: 'all 0.3s ease'
                  }}
                >
                  {type.icon}
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', marginBottom: '0.125rem' }}>
                    <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: '#1e293b', margin: 0, truncate: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                      {type.name}
                    </h3>
                    {isSelected && (
                      <CheckCircle2 size={14} color="#10b981" />
                    )}
                  </div>
                  <p style={{ fontSize: '0.6875rem', fontWeight: 600, color: '#64748b', marginBottom: '0.125rem' }}>
                    {type.subtitle}
                  </p>
                </div>
              </div>

              {/* Description - Shorter */}
              <p style={{ fontSize: '0.6875rem', color: '#374151', lineHeight: 1.3, margin: '0 0 0.5rem 0', height: '2.6rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                {type.description}
              </p>

              {/* Examples Section - Very compact */}
              <div>
                <p style={{
                  fontSize: '0.6875rem',
                  fontWeight: 600,
                  color: '#64748b',
                  marginBottom: '0.375rem'
                }}>
                  Exempel:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                  {type.examples.slice(0, isHovered ? 4 : 2).map((example, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '0.125rem 0.375rem',
                        fontSize: '0.625rem',
                        fontWeight: 500,
                        borderRadius: '10px',
                        border: `1px solid ${type.borderColor}`,
                        backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.8)',
                        color: type.color,
                        transition: 'all 0.2s ease',
                        display: 'inline-block',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {example}
                    </span>
                  ))}
                  {!isHovered && type.examples.length > 2 && (
                    <span style={{
                      fontSize: '0.625rem',
                      color: '#64748b',
                      padding: '0.125rem 0.25rem'
                    }}>
                      +{type.examples.length - 2}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Dashed Line Separator */}
      <div style={{
        width: '100%',
        height: '1px',
        background: 'repeating-linear-gradient(to right, transparent 0, transparent 4px, #cbd5e1 4px, #cbd5e1 8px)',
        margin: '2rem 0'
      }}></div>

      {/* Manual Entry Option */}
      <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
        <button
          onClick={() => onManualEntry && onManualEntry()}
          style={{
            background: 'none',
            border: '1px solid #e2e8f0',
            color: '#64748b',
            fontSize: '0.875rem',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            textDecoration: 'none'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = '#3b82f6';
            e.currentTarget.style.borderColor = '#3b82f6';
            e.currentTarget.style.backgroundColor = '#f8fafc';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = '#64748b';
            e.currentTarget.style.borderColor = '#e2e8f0';
            e.currentTarget.style.backgroundColor = 'transparent';
          }}
          title="Skapa verifikat manuellt utan mallar"
        >
          <Edit3 size={16} />
          Manuell inmatning
        </button>
      </div>
    </div>
  );
};

export default EntryTypeStep;



// import React, { useState } from "react";
// import { Info, CheckCircle2, TrendingUp, ShoppingCart, CreditCard, FileText } from "lucide-react";

// const EntryTypeStep = ({ selectedType, onSelect }) => {
//   const [hoveredType, setHoveredType] = useState(null);

//   const entryTypes = [
//     {
//       id: "income",
//       name: "Intäkter",
//       subtitle: "Pengar in",
//       description: "Försäljning, fakturor, betalningar från kunder",
//       examples: ["Försäljningsintäkter", "Konsultarvoden", "Ränteintäkter", "Hyresintäkter"],
//       icon: <TrendingUp size={32} />,
//       color: "#10b981",
//       bgColor: "#ecfdf5",
//       borderColor: "#d1fae5",
//       activeBgColor: "#d1fae5",
//       activeBorderColor: "#10b981",
//       commonScenarios: "Vanligt när du får betalt för produkter eller tjänster"
//     },
//     {
//       id: "expense",
//       name: "Kostnader",
//       subtitle: "Pengar ut",
//       description: "Inköp, kostnader, leverantörsbetalningar",
//       examples: ["Kontorsmaterial", "Hyra", "Telefon", "Marknadsföring"],
//       icon: <ShoppingCart size={32} />,
//       color: "#ef4444",
//       bgColor: "#fef2f2",
//       borderColor: "#fecaca",
//       activeBgColor: "#fecaca",
//       activeBorderColor: "#ef4444",
//       commonScenarios: "Vanligt för alla utgifter och inköp företaget gör"
//     },
//     {
//       id: "transfer",
//       name: "Överföringar",
//       subtitle: "Mellan konton",
//       description: "Flytta pengar mellan bankkonton eller kassor",
//       examples: ["Bank till bank", "Kassa till bank", "Sparkonto", "Valutaväxling"],
//       icon: <CreditCard size={32} />,
//       color: "#3b82f6",
//       bgColor: "#eff6ff",
//       borderColor: "#dbeafe",
//       activeBgColor: "#dbeafe",
//       activeBorderColor: "#3b82f6",
//       commonScenarios: "När du flyttar pengar utan att påverka resultat"
//     },
//     {
//       id: "other",
//       name: "Övrigt",
//       subtitle: "Specialposter",
//       description: "Justeringar, rättelser eller andra speciella poster",
//       examples: ["Avskrivningar", "Periodiseringar", "Rättelser", "Valutajusteringar"],
//       icon: <FileText size={32} />,
//       color: "#8b5cf6",
//       bgColor: "#faf5ff",
//       borderColor: "#e9d5ff",
//       activeBgColor: "#e9d5ff",
//       activeBorderColor: "#8b5cf6",
//       commonScenarios: "För avancerade bokföringsposter och justeringar"
//     },
//   ];

//   return (
//     <div className="enhanced-step-content">
//       {/* Header Section */}
//       <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
//         <h2 style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b', marginBottom: '0.75rem' }}>
//           Välj Posttyp
//         </h2>
//         <p style={{ fontSize: '1rem', color: '#64748b', marginBottom: '1rem' }}>
//           Vilken typ av bokföringspost vill du skapa?
//         </p>

//         <div className="info-card">
//           <Info className="info-icon" size={20} />
//           <p className="info-text">
//             <strong>Osäker på vad du ska välja?</strong> Oroa dig inte! De flesta poster är antingen intäkter (pengar in) eller kostnader (pengar ut).
//             Hovra över korten för att se fler exempel.
//           </p>
//         </div>
//       </div>

//       {/* Entry Type Grid */}
//       <div style={{
//         display: 'grid',
//         gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
//         gap: '1.5rem',
//         marginBottom: '2rem'
//       }}>
//         {entryTypes.map((type) => {
//           const isSelected = selectedType === type.id;
//           const isHovered = hoveredType === type.id;

//           return (
//             <div
//               key={type.id}
//               style={{
//                 backgroundColor: isSelected ? type.activeBgColor : type.bgColor,
//                 borderColor: isSelected ? type.activeBorderColor : type.borderColor,
//                 borderRadius: '12px',
//                 border: '2px solid',
//                 padding: '1.5rem',
//                 cursor: 'pointer',
//                 transition: 'all 0.3s ease',
//                 transform: isSelected ? 'translateY(-2px)' : isHovered ? 'translateY(-4px)' : 'translateY(0)',
//                 boxShadow: isSelected ? '0 8px 25px rgba(0, 0, 0, 0.15)' : isHovered ? '0 10px 25px rgba(0, 0, 0, 0.1)' : 'none'
//               }}
//               onClick={() => onSelect(type.id)}
//               onMouseEnter={() => setHoveredType(type.id)}
//               onMouseLeave={() => setHoveredType(null)}
//             >
//               {/* Card Header */}
//               <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1rem' }}>
//                 <div
//                   style={{
//                     padding: '0.75rem',
//                     borderRadius: '12px',
//                     backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.7)',
//                     color: type.color,
//                     flexShrink: 0,
//                     transform: isHovered ? 'scale(1.1)' : 'scale(1)',
//                     transition: 'all 0.3s ease'
//                   }}
//                 >
//                   {type.icon}
//                 </div>

//                 <div style={{ flex: 1 }}>
//                   <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
//                     <h3 style={{ fontSize: '1.25rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
//                       {type.name}
//                     </h3>
//                     {isSelected && (
//                       <CheckCircle2 size={20} color="#10b981" />
//                     )}
//                   </div>
//                   <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#64748b', marginBottom: '0.5rem' }}>
//                     {type.subtitle}
//                   </p>
//                   <p style={{ color: '#374151', lineHeight: 1.5, margin: 0 }}>
//                     {type.description}
//                   </p>
//                 </div>
//               </div>

//               {/* Examples Section */}
//               <div style={{ marginBottom: '1rem' }}>
//                 <p style={{
//                   fontSize: '0.875rem',
//                   fontWeight: 600,
//                   color: '#64748b',
//                   marginBottom: '0.5rem'
//                 }}>
//                   Vanliga exempel:
//                 </p>
//                 <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
//                   {type.examples.slice(0, isHovered ? type.examples.length : 4).map((example, index) => (
//                     <span
//                       key={index}
//                       style={{
//                         padding: '0.25rem 0.75rem',
//                         fontSize: '0.75rem',
//                         fontWeight: 500,
//                         borderRadius: '20px',
//                         border: `1px solid ${type.borderColor}`,
//                         backgroundColor: isSelected ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.8)',
//                         color: type.color,
//                         transition: 'all 0.2s ease',
//                         display: 'inline-block'
//                       }}
//                     >
//                       {example}
//                     </span>
//                   ))}
//                   {!isHovered && type.examples.length > 4 && (
//                     <span style={{
//                       fontSize: '0.75rem',
//                       color: '#64748b',
//                       padding: '0.25rem 0.5rem'
//                     }}>
//                       +{type.examples.length - 4} fler...
//                     </span>
//                   )}
//                 </div>
//               </div>
//             </div>
//           );
//         })}
//       </div>

//       {/* Selected Type Info 
//       {selectedType && (
//         <div className="form-section">
//           <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
//             <CheckCircle2 size={24} color="#10b981" />
//             <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#1e293b', margin: 0 }}>
//               Bra val! Du har valt: {entryTypes.find(t => t.id === selectedType)?.name}
//             </h3>
//           </div>
//           <p style={{ color: '#374151', marginBottom: '1rem' }}>
//             {entryTypes.find(t => t.id === selectedType)?.description}
//           </p>
//           <div style={{
//             background: '#eff6ff',
//             border: '1px solid #bfdbfe',
//             borderRadius: '8px',
//             padding: '1rem'
//           }}>
//             <p style={{ fontSize: '0.875rem', color: '#1e40af', margin: 0 }}>
//               <strong>Nästa steg:</strong> Du kan lägga till bilagor (som kvitton eller fakturor) eller gå direkt till mallval för att fortsätta.
//             </p>
//           </div>
//         </div>
//       )}*/}


//     </div>
//   );
// };

// export default EntryTypeStep;